import React, { type ReactElement, useState } from 'react';
import { Heading, Box, Text, Stack, TextLinkButton } from 'braid-design-system';

import * as styles from './ContentWithListContainer.css';
import type { IJobApplicationSummary } from '../../../stores/JobApplicationStore/JobApplicationSummary';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { trackShowMoreCandidatesInBulkActionPressed } from 'src/utils/events';

interface Props {
  children: ReactElement | null;
  selectedJobApplications: IJobApplicationSummary[];
  listViewHeading: string;
  listContainerClassName?: string;
}

const ContentWithListContainer = ({
  listViewHeading,
  children,
  selectedJobApplications,
  listContainerClassName = '',
}: Props) => {
  const MAX_CANDIDATE_LIST = 10;
  const [showRemaining, setShowRemaining] = useState(false);
  const { t } = useTranslations(translations);

  let defaultMaxSelectedApplications = selectedJobApplications;
  let remainingSelectedApplications: IJobApplicationSummary[] = [];

  if (selectedJobApplications.length > MAX_CANDIDATE_LIST) {
    defaultMaxSelectedApplications = selectedJobApplications.slice(
      0,
      MAX_CANDIDATE_LIST,
    );
    remainingSelectedApplications =
      selectedJobApplications.slice(MAX_CANDIDATE_LIST);
  }

  return (
    <Box display="flex" height="full">
      <Box padding="xlarge" flexGrow={1} maxWidth="small">
        {children}
      </Box>
      <Box
        flexDirection="column"
        display="flex"
        className={[styles.listContainer, listContainerClassName]}
        paddingY="xlarge"
        paddingX="medium"
        background="body"
      >
        <Stack space="gutter">
          <Heading level="4">{listViewHeading}</Heading>
          <Box flexGrow={1}>
            <Stack space="small">
              {defaultMaxSelectedApplications.map(
                (item: IJobApplicationSummary) => (
                  <Box paddingBottom="xsmall" key={item.id}>
                    <Text>
                      {item.firstName} {item.lastName}
                    </Text>
                  </Box>
                ),
              )}
              {remainingSelectedApplications.length > 0 && !showRemaining && (
                <Text>
                  <TextLinkButton
                    onClick={() => {
                      trackShowMoreCandidatesInBulkActionPressed();
                      setShowRemaining(true);
                    }}
                    hitArea="large"
                  >
                    {t('Show remainingSelectedApplicationCount more', {
                      remainingSelectedApplicationCount:
                        remainingSelectedApplications.length,
                    })}
                  </TextLinkButton>
                </Text>
              )}
              {showRemaining && (
                <>
                  {remainingSelectedApplications.map(
                    (item: IJobApplicationSummary) => (
                      <Box paddingBottom="xsmall" key={item.id}>
                        <Text>
                          {item.firstName} {item.lastName}
                        </Text>
                      </Box>
                    ),
                  )}
                  <Text>
                    <TextLinkButton
                      onClick={() => setShowRemaining(false)}
                      hitArea="large"
                    >
                      {t('Show less')}
                    </TextLinkButton>
                  </Text>
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default ContentWithListContainer;
